import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CLoseIcon from "../../../icons/close-icon.svg";
import { showSpinner, closeSpinner, modal } from "../../../js/modules/Dialogs";
import { setQuote } from "../../../services/adminService";
import ReactSelect from "react-select";
import { sessionExpired } from "../../../config/messages";
import { toScreen } from "../../../js/modules/Routing";
import { toast } from "react-toastify";
import { CloseIcon } from "../../commonUI/Icons/SvgIcon";

const QuoteEdit = ({ view, quote, onClose, props,quotePriceForCommission,quotePriceForMav }) => {
  const isEditQuote = view === "editquotedetails";
  const navigate = useNavigate();
  const defaultCommission = 0.2; 

  const [formData, setFormData] = useState({
    status: "",
    numberOfVans: "",
    numberOfMen: "",
    priceQuoted: "",
    mavFee: "",
    costForCustomer: "",
   
  });

  useEffect(() => {
    if (quote) {
      const priceQuoted = quotePriceForMav || 0;
      const mavFee = quotePriceForCommission || 0;
      const costForCustomer = priceQuoted + mavFee;

      setFormData({
        status: quote?.status || "",
        numberOfVans: quote.quoteSpec?.numberVans?.toString() || "",
        numberOfMen: quote.quoteSpec?.numberMen?.toString() || "",
        priceQuoted: priceQuoted.toString(),
        mavFee: mavFee.toFixed(2),
        costForCustomer: costForCustomer.toFixed(2),
      });
    }
  }, [quote, quotePriceForCommission,quotePriceForMav]);

  const statusOptions = [
    { value: "open", label: "Open" },
    { value: "closed", label: "Closed" },
  ];

  const handleStatusChange = (selectedOption) => {
    if (selectedOption) {
      setFormData((prev) => ({
        ...prev,
        status: selectedOption.value,
      }));
    }
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    const inputValue = type === "checkbox" ? checked : value;

    setFormData((prev) => {
      const newFormData = { ...prev, [name]: inputValue };

       if (name === "priceQuoted") {
        const priceQuoted = parseFloat(inputValue) || 0;
        const commission = parseFloat(quote.job?.commission) || defaultCommission;
        const mavFee = priceQuoted * commission;
        const costForCustomer = priceQuoted + mavFee;
        newFormData.mavFee = mavFee.toFixed(2);
        newFormData.costForCustomer = costForCustomer.toFixed(2);
      }

      return newFormData;
    });
  };

  const saveQuoteDone = useCallback(
    (resStatus, responseString, resJson) => {
      closeSpinner();

      switch (resStatus) {
        case 401:
          modal(sessionExpired);
          toScreen(false, navigate, "/logout");
          break;
        case 400:
          modal(responseString);
          break;
        case 200:
          toast.success("Quote successfully updated");
          if (onClose) onClose();
          // Optionally refresh the quotes page
          // toScreen(false, navigate, "/quotes");
          break;
        default:
          toast.error("An error occurred. Please try again.");
      }
    },
    [navigate, onClose]
  );

  const validateForm = () => {
    const errors = [];

    if (!formData.status) {
      errors.push("Status is required");
    }

    if (!formData.numberOfVans || parseInt(formData.numberOfVans) < 1) {
      errors.push("Number of vans must be at least 1");
    }

    if (!formData.numberOfMen || parseInt(formData.numberOfMen) < 1) {
      errors.push("Number of men must be at least 1");
    }

    if (!formData.priceQuoted || parseFloat(formData.priceQuoted) <= 0) {
      errors.push("Price quoted must be greater than 0");
    }

    return errors;
  };

  // Save quote data
  const saveQuote = useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
      }

      // Validate form
      const errors = validateForm();
      if (errors.length > 0) {
        modal(`Please fix the following errors:\n• ${errors.join('\n• ')}`);
        return;
      }

      showSpinner();

      // Prepare quote data for API
      const theQuote = {
        _id: quote._id,
        short_id: quote?.short_id,
        notes: quote?.notes,
        mavPayment: quote?.job?.mavPayment,
        quoteSpec: {
          price: parseFloat(formData?.priceQuoted),
          numberMen: parseInt(formData?.numberOfMen),
          numberVans: parseInt(formData?.numberOfVans),
          deleteImages: quote.quoteSpec?.deleteImages || false,
        },
        commission: parseFloat(formData?.mavFee) / parseFloat(formData?.priceQuoted),
        status: formData?.status,
      };

      setQuote(theQuote, saveQuoteDone, props?.userDetails?.jwttoken);
    },
    [props?.userDetails, quote, saveQuoteDone, formData, validateForm]
  );

  return (
    <div className="right-modal discount-modal">
      {/* Modal Header */}
      <div className="modal-header">
        <div className="modal-title">
          <h2>{isEditQuote && "Edit Quote Details"}</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
      </div>

      {/* Modal Body */}
      <div className="modal-body">
        {isEditQuote && (
          <form onSubmit={saveQuote}>
            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status 
              </label>
              <ReactSelect
                classNamePrefix="custom-select"
                options={statusOptions}
                name="status"
                placeholder="Select Status"
                value={statusOptions?.find((opt) => opt.value === formData?.status)}
                onChange={handleStatusChange}
                className="react-select-container"
              />
            </div>

            <div className="form-group">
              <label htmlFor="numberOfVans" className="form-label">
                Number of Vans 
              </label>
              <input
                type="number"
                id="numberOfVans"
                name="numberOfVans"
                className="form-control"
                placeholder="Enter number of vans"
                value={formData?.numberOfVans}
                onChange={handleInputChange}
                min="1"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="numberOfMen" className="form-label">
                Number of Men 
              </label>
              <input
                type="number"
                id="numberOfMen"
                name="numberOfMen"
                className="form-control"
                placeholder="Enter number of men"
                value={formData?.numberOfMen}
                onChange={handleInputChange}
                min="1"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="priceQuoted" className="form-label">
                Price Quoted
              </label>
              <input
                type="number"
                id="priceQuoted"
                name="priceQuoted"
                className="form-control"
                placeholder="Enter quoted price"
                value={formData?.priceQuoted}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                required
              />
            </div>

        
            <div className="form-group">
              <label htmlFor="mavFee" className="form-label">
              MAV Fee ( {(quote.job?.commission || defaultCommission) * 100}%)
              </label>
              <input
                type="number"
                id="mavFee"
                name="mavFee"
                className="form-control"
                placeholder="Calculated MAV fee"
                value={formData?.mavFee}
                readOnly
                disabled
              />
            </div>

            <div className="form-group">
              <label htmlFor="costForCustomer" className="form-label">
              Cost for Customer
              </label>
              <input
                type="number"
                id="costForCustomer"
                name="costForCustomer"
                value={formData?.costForCustomer}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Total cost including fees"
           
                
             
              />
            </div>
          </form>
        )}
      </div>

          {/* <div className="modal-footer">
        <button className="btn btn-outline-primary" onClick={onClose}>
          Close
        </button>
        {selectedIssueResolution?.refund === "pending" && (
          <div>
            <button
              className="btn btn-danger"
              style={{ marginRight: 12 }}
              onClick={() =>
                handleFetchRefundPayment({
                  paymentChargeId:
                    selectedIssueResolution?.jobDetails?.stripeChargeId,
                  decision: "rejected",
                })
              }
            >
              Reject Refund
            </button>
            <button
              className="btn btn-success"
              onClick={() =>
                handleFetchRefundPayment({
                  paymentChargeId:
                    selectedIssueResolution?.jobDetails?.stripeChargeId,
                  decision: "approved",
                })
              }
            >
              Approve Refund
            </button>
          </div>
        )}
      </div> */}

      {/* Modal Footer */}
      <div className="modal-footer">
        <button className="btn btn-outline-primary" onClick={onClose}>
          Cancel
        </button>
        <div className="d-flex align-items-center g-12">
          <button
            className="btn btn-success"
            type="button"
            onClick={saveQuote}
          >
            Save Changes
          </button>
         
        </div>
      </div>
    </div>
  );
};

export default QuoteEdit;
